import type { HintProps, TooltipProps } from '@repo/go-ui';
import { ActionsBar, Hint, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

interface Props {
  outerTitle: string;
  innerTitle: (close: () => void) => ReactNode;
  icon: ReactElement;
  disabled?: boolean;
  handleClick?: (value: boolean) => void;
  hintProps?: HintProps<HTMLDivElement>;
}

export const DoubleTooltipSidebarItem = observer(
  ({ outerTitle, innerTitle, icon, disabled, handleClick, hintProps }: Props) => {
    const [isInnerTooltipOpen, setInnerTooltipOpen] = useState(false);
    const [isExternalTooltipOpen, setExternalTooltipOpen] = useState(false);

    function close() {
      setInnerTooltipOpen(false);
    }

    const handleExternalTooltipOpenChange = (value: boolean) => {
      if (isInnerTooltipOpen) {
        setExternalTooltipOpen(false);
        return;
      }

      setExternalTooltipOpen(value);
    };

    const handleInnerTooltipOpenChange = (value: boolean) => {
      handleExternalTooltipOpenChange(false);

      setInnerTooltipOpen(value);
      handleClick?.(value);
    };

    const tooltips: (Omit<TooltipProps<HTMLElement>, 'children'> & {
      key: string;
    })[] = [
      {
        key: 'tooltip',
        trigger: ['hover'],
        placement: 'left',
        open: isExternalTooltipOpen && !hintProps?.open,
        onOpenChange: (value) => handleExternalTooltipOpenChange(value),
        title: outerTitle,
        delay: 400,
      },
      {
        key: 'popover',
        trigger: ['click'],
        title: innerTitle(close),
        placement: 'left',
        open: isInnerTooltipOpen,
        onPointerDown: (e) => e.stopPropagation(),
        onOpenChange: (value) => handleInnerTooltipOpenChange(value),
        style: { padding: 0, borderRadius: 4 },
      },
    ];

    const multi = (
      <Tooltip.Multi tooltips={tooltips}>
        <ActionsBar.Item
          disabled={disabled}
          active={isInnerTooltipOpen}
          onClick={() => handleInnerTooltipOpenChange(!isInnerTooltipOpen)}
        >
          {hintProps ? <Hint {...hintProps}>{icon}</Hint> : icon}
        </ActionsBar.Item>
      </Tooltip.Multi>
    );

    return multi;
  },
);
